import * as Cookies from "es-cookie";

import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  HttpLink,
} from "apollo-boost";
import { onError } from "apollo-link-error";

import { endSessionAndRedirect } from "./utils/session";

/**
 * The one Apollo client, built here rather than in index.js so that code outside
 * the component tree can import it. The notifications store needs it to fetch
 * once per session instead of once per page (see utils/notificationsStore.js);
 * previously nothing but a mounted component could issue a query.
 */

// const httpLink = new HttpLink({ uri: process.env.REACT_APP_API_URL });
// const httpLink = new HttpLink({ uri: "https://api.rizee.in/graphql" });
const httpLink = new HttpLink({ uri: "https://rizee-student-backend-dev1-755214126997.us-south1.run.app/graphql" });

// const httpLink = new HttpLink({ uri: "http://localhost:4005/graphql" });

// Attach the bearer token per request. The token is read from the cookie
// inside the link, on every operation, not once at construction: the client is
// built once at page load, so deciding auth up front (as this did before) meant
// a user who logged in during that load kept an unauthenticated client until a
// full page reload, and every query went out with no Authorization header.
// Reading per-operation also means a refreshed token is picked up immediately.
//
// When there is no token the header is simply omitted, which is correct for the
// public operations (studentLogin, forgotPassword, the OTP mutations) that must
// work before one exists.
const authLink = new ApolloLink((operation, forward) => {
  const token = Cookies.get("studenttoken");
  operation.setContext({
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  return forward(operation);
});

// Send the user back to login whenever the API says the request was not
// authenticated. The server's graphqlAuth plugin rejects any non-public
// operation without a valid token — no Authorization header, an expired token,
// or a revoked one — and tags the error `UNAUTHENTICATED`.
//
// Handling it here rather than per page matters because the page-level guards
// only test whether the studenttoken cookie *exists*. A token that has expired
// still exists, so those guards pass, the screen renders, and every query on it
// fails: the user sees a page of "Server Error. Authorization header not"
// alerts with no way forward. Matching on extensions.code, not the message
// text, so this keeps working if the wording changes.
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const unauthenticated =
    (graphQLErrors || []).some(
      e => e && e.extensions && e.extensions.code === "UNAUTHENTICATED"
    ) ||
    // A 401 never reaches graphQLErrors — apollo-link-http surfaces a non-2xx
    // response as a network error instead.
    (networkError && networkError.statusCode === 401);

  if (unauthenticated) endSessionAndRedirect();
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
